// Graph Builder用のユーティリティ関数

import { DataSet, ColumnSchema, ColumnId, DatasetWithData } from '../../types';
import { DatasetAccessor } from './DatasetAccessor';

/**
 * 数値型の列のみを取得
 */
export function getNumericColumns(dataset: DataSet): ColumnSchema[] {
  return dataset.schema.columns.filter(col => 
    col.type === 'int64' || col.type === 'float64'
  );
}

/**
 * interval/ratio尺度の数値型列のみを取得（ヒストグラムや散布図用）
 */
export function getIntervalRatioColumns(dataset: DataSet): ColumnSchema[] {
  return dataset.schema.columns.filter(col => {
    if (col.type !== 'int64' && col.type !== 'float64') return false;
    const scale = col.scale?.scale;
    // 尺度が未設定の場合はデフォルトでintervalとみなす
    return !scale || scale === 'interval' || scale === 'ratio';
  });
}

/**
 * カテゴリカル型の列を取得（文字列、ブール値、またはnominal/ordinal尺度）
 */
export function getCategoricalColumns(dataset: DataSet): ColumnSchema[] {
  return dataset.schema.columns.filter(col => {
    // 文字列やブール値は常にカテゴリカル
    if (col.type === 'string' || col.type === 'boolean') return true;
    // nominal/ordinal尺度の数値もカテゴリカル
    const scale = col.scale?.scale;
    return scale === 'nominal' || scale === 'ordinal';
  });
}

/**
 * 日付型の列を取得
 */
export function getDateColumns(dataset: DataSet): ColumnSchema[] {
  return dataset.schema.columns.filter(col => 
    col.type === 'datetime'
  );
}

/**
 * すべての列を取得
 */
export function getAllColumns(dataset: DataSet): ColumnSchema[] {
  return dataset.schema.columns;
}

/**
 * 列のユニーク値数を取得
 * @param dataset データセット（dataプロパティが必須）
 * @param columnId 列ID
 * @returns ユニーク値の数（nullを除く）
 */
export function getUniqueValueCount(dataset: DatasetWithData, columnId: ColumnId): number {
  const accessor = new DatasetAccessor(dataset);
  const columnData = accessor.getColumnDataById(columnId);
  const uniqueValues = new Set(columnData.filter(v => v !== null && v !== undefined));
  return uniqueValues.size;
}

/**
 * 複数列のユニーク値数をまとめて取得
 * @param dataset データセット（dataプロパティが必須）
 * @param columnIds 列IDの配列
 * @returns 列ID -> ユニーク値数のMap
 */
export function getUniqueValueCounts(dataset: DatasetWithData, columnIds: ColumnId[]): Map<ColumnId, number> {
  const accessor = new DatasetAccessor(dataset);
  const result = new Map<ColumnId, number>();

  for (const columnId of columnIds) {
    const columnData = accessor.getColumnDataById(columnId);
    const uniqueValues = new Set(columnData.filter(v => v !== null && v !== undefined));
    result.set(columnId, uniqueValues.size);
  }

  return result;
}